/* eslint-disable no-restricted-syntax */
import { mapGetters, mapActions, mapState } from 'vuex'

// utils
import utils from '@/util'

// api
import FolderApi from '@/api/folder'
import presentationApi from '@/api/presentation'

import configs from '@/config'
import constants from '@/constant'

const { MAX_PRESENTATION_IN_FOLDER } = configs
const { eventCategories } = constants

export default {
  data() {
    return {
      loadingFolderTree: false,
      maxPresentationInFolder: MAX_PRESENTATION_IN_FOLDER,
      showingDelelePresentationsModal: false,
      isShowingMovePresnModal: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      workingMode: (state) => state.workingMode,
      currentFolderDepth: (state) => state.currentFolderDepth,
      presentations: (state) => state.presentations,
      currentFolder: (state) => state.currentFolder,
    }),
    ...mapState('movePresentation', {
      destinatedFolderId: (state) => state.destinatedFolderId,
      selectedPresentationsV2: (state) => state.selectedPresentations,
      tabContent: (state) => state.tabContent,
      showingModalWarning: (state) => state.showingModalWarning,
      isShowingMovePresnModalV2: (state) => state.isShowingMovePresnModal,
    }),
    ...mapGetters('featureToggles', ['canUseMovePresentations']),
    ...mapGetters('movePresentation', ['folders', 'selectedPresentationsId', 'destinationFolder']),
    isPersonalWorkingMode() {
      return this.workingMode === 'personal'
    },
    localDestinatedFolderId: {
      get() {
        return this.destinatedFolderId
      },
      set(value) {
        this.updateDestinatedFolderId(value)
      },
    },
    onlyRootOrNone() {
      return !this.folders.length || !this.folders.some((p) => p.folderId != null)
    },
    parseIntMaxPresentationInFolder() {
      return parseInt(MAX_PRESENTATION_IN_FOLDER, 10)
    },
    modalTitle() {
      const selectedPresnLength = this.selectedPresentationsV2.length
      if (selectedPresnLength === 1) {
        return this.$i18nPresenter.t('Move this presentation to…')
      }
      return this.$i18nPresenter.t('moveXPresentationsTo', {
        length: selectedPresnLength,
      })
    },
    destinationFolderName() {
      return this.folders.find((folder) => folder.folderId === this.destinatedFolderId).folderName
    },
    currentFolderName() {
      return this.currentFolder?.folderName
    },
  },
  methods: {
    ...mapActions([
      'setPresentationsAction',
    ]),
    ...mapActions('movePresentation', [
      'setMyPresentationFoldersAction',
      'setSharedWithMeFoldersAction',
      'updateDestinatedFolderId',
      'updateIsAllPresentationsSelected',
      'resetDestinatedFolder',
      'updateMovePresentationLoading',
      'updateShowingModalWarning',
      'updateIsShowingMovePresnModal',
    ]),
    async getAndSetMyPresentationFolders() {
      this.updateMovePresentationLoading(true)
      const folderTree = await FolderApi.getFullFolderTree()
      const flatFolderTree = this._flattenFolderTree(folderTree)

      flatFolderTree.unshift({ folderId: null, folderName: this.$i18nPresenter.t('myPresentations') })
      const folders = this.updateFlatFolderTree(flatFolderTree)
      this.setMyPresentationFoldersAction(folders)
      this.resetDestinatedFolder()
      this.updateMovePresentationLoading(false)
    },
    async getAndSetSharedWithMeFolders() {
      this.updateMovePresentationLoading(true)
      const folderTree = await FolderApi.getEditableFolderTrees()
      const updatedDepthFolderTree = this.updateDepthFolderTree(folderTree)
      const flatFolderTree = this._flattenFolderTree(updatedDepthFolderTree)

      const folders = this.updateFlatFolderTree(flatFolderTree)
      this.setSharedWithMeFoldersAction(folders)
      this.resetDestinatedFolder()
      this.updateMovePresentationLoading(false)
    },
    updateFlatFolderTree(flatFolderTree) {
      const hasNonOwnerPresentation =
        this.selectedPresentationsV2 ?
          this.selectedPresentationsV2.some((p) => p.userId !== this.currentUser.id) :
          false
      return flatFolderTree.map((f) => {
        const isNotOwnerAndRootFolder = hasNonOwnerPresentation && f.folderId === null
        return {
          ...f,
          disabled: this.selectedPresentationsV2.some((p) => (p.folderId === f.folderId)
            || (f.countPresentation === this.parseIntMaxPresentationInFolder && f.depth && f.depth > 0))
            || isNotOwnerAndRootFolder,
          folderName: this.selectedPresentationsV2.some((p) => p.folderId === f.folderId) ? `${f.folderName} (current)` : f.folderName,
        }
      })
    },
    _flattenFolderTree(folderTree) {
      return folderTree.reduce((acc, {
        folderId,
        folderName,
        parentFolderId,
        depth,
        folders,
        countPresentation,
        folderShareId,
        folderInviteId,
        hasShared,
      }) => {
        acc.push({
          folderId,
          folderName,
          parentFolderId,
          depth,
          countPresentation,
          folderShareId,
          folderInviteId,
          hasShared,
        })
        if (folders && folders.length > 0) {
          acc = acc.concat(this._flattenFolderTree(folders))
        }
        return acc
      }, [])
    },
    async handleMovePresn() {
      if (!this.currentFolder?.hasShared && !this.destinationFolder.hasShared) {
        this.movePresentations()
        return
      }
      this.updateShowingModalWarning(true)
    },
    async movePresentations() {
      try {
        const payload = {
          presentationIds: this.selectedPresentationsId,
          folderId: this.destinatedFolderId,
        }
        await presentationApi.movePresentations(payload)

        const newPresentations = this.presentations.filter((p) => !this.selectedPresentationsId.includes(p.id))
        this.setPresentationsAction(newPresentations)

        let folderName = 'My presentations'
        if (this.destinatedFolderId) {
          folderName = this.destinationFolderName
        }

        if (this.selectedPresentationsV2 && this.selectedPresentationsV2.length === 1) {
          utils.showToastSuccess(`"${this.selectedPresentationsV2[0].name}" ${this.$i18nPresenter.t('moved to')} "${utils.sanitize(folderName)}"`)
        } else if (this.selectedPresentationsV2 && this.selectedPresentationsV2.length > 1) {
          utils.showToastSuccess(`${this.selectedPresentationsV2.length} ${this.$i18nPresenter.t('presentations moved to')} "${utils.sanitize(folderName)}"`)
        }

        this.isShowingMovePresnModal = false
        this.updateIsShowingMovePresnModal(false)
        this.updateShowingModalWarning(false)
      } catch (error) {
        utils.showToastError(this.$i18nPresenter.t('Move presentations failed'))
      }
    },
    updateDepthFolderTree(folderTree, depth = 0) {
      for (const folder of folderTree) {
        folder.depth = depth
        if (folder.folders && folder.folders.length > 0) {
          this.updateDepthFolderTree(folder.folders, folder.depth + 1)
        }
      }
      return folderTree
    },
    async handleMoveToItemClick() {
      this.isShowingMovePresnModal = true
      this.updateIsShowingMovePresnModal(true)
    },
    handleHideMovePresentation() {
      this.isShowingMovePresnModal = false
      this.updateIsShowingMovePresnModal(false)
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentation Kebab Menu > Move presentation > Close modal')
    },
    async handleHideDeletePresentations() {
      this.showingDelelePresentationsModal = false
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentation Kebab Menu > Delete presentation > Close modal')
    },
    async handleDeletePresnItemClick() {
      this.showingDelelePresentationsModal = true
    },
    handleAllPresentationSelectV2(value) {
      this.updateIsAllPresentationsSelected(value)
      const newPresentations = this.presentations.map((p) => {
        if (p.canMoveSomewhere) {
          return { ...p, selected: value }
        }
        return p
      })
      this.setPresentationsAction(newPresentations)
    },
    handleHideModalWarning() {
      this.updateShowingModalWarning(false)
    },
  },
}
